import { toast } from 'sonner';

import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { getQueryKey } from '@/hooks/useQueryKeys';
import { queryClient } from '@/queryclient';
import { FlagType } from '@/types';
import { useMutation } from '@/utils/api';

export const useFlag = ({
  doNotImpersonate,
  disableCache,
  email,
}: {
  doNotImpersonate?: boolean;
  disableCache?: boolean;
  email?: string;
} = {}) => {
  const { data: flagsResponse, isLoading } = useMy(
    { resource: 'flag' },
    {
      doNotImpersonate,
      email,
      ...(disableCache
        ? {
            staleTime: 0, // Data is immediately stale
            gcTime: 0, // Don't cache the data
            refetchOnMount: 'always' as const, // Always refetch when component mounts
            refetchOnWindowFocus: true, // Refetch when window gains focus
          }
        : {}),
    }
  );
  const enableBreachAndAttack = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_BREACH_AND_ATTACK
  );
  const disableAIMLTraining = flagsResponse?.find(
    flag => flag.name === FlagType.DISABLE_AIMI_TRAINING
  );
  const disableAIInference = flagsResponse?.find(
    flag => flag.name === FlagType.DISABLE_AI_INFERENCE
  );
  const enablePersonalTenancy = flagsResponse?.find(
    flag =>
      flag.name === FlagType.ENABLE_PERSONAL_TENANCY ||
      flag.key.endsWith(FlagType.ENABLE_PERSONAL_TENANCY)
  );
  const enableCloud = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_CLOUD
  );
  const enableUsers = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_USERS
  );
  const enableDevice = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_DEVICE
  );
  const enableExternal = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_EXTERNAL
  );
  const enableCode = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_CODE
  );
  const enableAIAgents = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_AI_AGENTS
  );
  // Hunt-visibility ("AI Automation") flag. Stable boolean regardless of the
  // underlying flag string. Used as a PRESENTATION hint only (gates the AI
  // Assistant Hunts tab affordance); the server remains the source of truth for
  // hunt data via the my.go HuntsVisible gate.
  const huntVisibility = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_AI_AUTOMATION
  );
  const enableKnossos = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_KNOSSOS
  );
  const enableLlm = flagsResponse?.find(
    flag => flag.name === FlagType.ENABLE_LLM
  );

  return {
    enableBreachAndAttack: !!enableBreachAndAttack,
    disableAIMLTraining: !!disableAIMLTraining,
    disableAIInference: !!disableAIInference,
    isFlagLoading: isLoading,
    enablePersonalTenancy: !!enablePersonalTenancy,
    enableCloud: !!enableCloud,
    enableUsers: !!enableUsers,
    enableDevice: !!enableDevice,
    enableExternal: !!enableExternal,
    enableCode: !!enableCode,
    enableAIAgents: !!enableAIAgents,
    enableKnossos: !!enableKnossos,
    huntVisibility: !!huntVisibility,
    enableLlm: !!enableLlm,
  };
};

/**
 * Update feature flags with automatic success toast
 *
 * Default behavior: Shows "Flags updated successfully" toast
 * Override: Pass { silent: true } in variables to suppress
 *
 * Migrated from showToast to silent pattern for consistency with other hooks.
 *
 * @example
 * // Standard usage (automatic success toast)
 * const { mutate } = useUpdateFlags();
 * mutate({ flagsToAdd: ['ENABLE_CLOUD'] });
 *
 * @example
 * // Silent update (no toast)
 * mutate({ flagsToAdd: ['ENABLE_CLOUD'], silent: true });
 *
 * @example
 * // Custom success message
 * mutate({
 *   flagsToAdd: ['ENABLE_CLOUD'],
 *   successMessage: 'Cloud module enabled'
 * });
 */
export const useUpdateFlags = ({ email }: { email?: string } = {}) => {
  const axios = useAxios({ email, doNotImpersonate: !!email });
  const axiosNotImpersonated = useAxios({ doNotImpersonate: true });
  const { invalidate: invalidateFlags } = useMy(
    { resource: 'flag' },
    { email, enabled: false }
  );

  return useMutation({
    defaultErrorMessage: 'Failed to update flags',
    mutationFn: async ({
      flagsToAdd = [],
      flagsToDelete = [],
      successMessage = 'Flags updated successfully',
      silent = false,
      email: overrideEmail,
    }: {
      flagsToAdd?: string[];
      flagsToDelete?: string[];
      successMessage?: string;
      silent?: boolean;
      email?: string;
    }) => {
      const targetEmail = overrideEmail ?? email;
      const scopedAxios = targetEmail ? axiosNotImpersonated : axios;
      const requestConfig = targetEmail
        ? { headers: { account: targetEmail } }
        : undefined;

      const addPromises = flagsToAdd
        .map(async flag => {
          const { data } = await scopedAxios.put(
            `/flag`,
            {
              name: flag,
            },
            requestConfig
          );
          return data;
        })
        .map(promise => promise.catch(error => error));
      const deletePromises = flagsToDelete
        .map(async flag => {
          const { data } = await scopedAxios.delete(`/flag`, {
            ...requestConfig,
            data: {
              name: flag,
            },
          });
          return data;
        })
        .map(promise => promise.catch(error => error));

      const addResponse = await Promise.all(addPromises);
      const deleteResponse = await Promise.all(deletePromises);

      const validResults = [...addResponse, ...deleteResponse].filter(
        result => !(result instanceof Error)
      );

      // Always invalidate flags to ensure UI updates, even if no changes were made
      invalidateFlags();
      if (targetEmail) {
        queryClient.invalidateQueries({
          queryKey: [...getQueryKey.getMy('flag'), targetEmail],
        });
      }

      // Show success toast unless suppressed
      if (validResults.length > 0 && !silent) {
        toast.success(successMessage);
      }

      return validResults;
    },
  });
};
